import React, { useState, useMemo, useRef, useEffect } from "react";
import { Flex, Text, Card, Table, Badge, Select, TextField, TextArea, Button, Box, Tooltip, SegmentedControl, Progress,
  AlertDialog, Popover, CheckboxGroup, Checkbox, Dialog, Callout, Switch } from "@radix-ui/themes";
import { MagnifyingGlassIcon, ReloadIcon, ChevronLeftIcon, ChevronRightIcon, PersonIcon, TrashIcon, CheckCircledIcon,
  CrossCircledIcon, PieChartIcon, PlusIcon, MixerHorizontalIcon, DownloadIcon, InfoCircledIcon, BookmarkIcon,
  Pencil1Icon, EnvelopeClosedIcon, Link2Icon, CopyIcon } from "@radix-ui/react-icons";
import { useProfileContext, useLocationContext } from "context";
import { useContacts, useContactsStats, useContactImports, useDeleteContact, useCreateContact, useImportContactsCsv,
  useContactTags, useUpdateContact, useEmailSuppressions, useClearEmailSuppression } from "hooks";
import { getContactDetail, getEmailActivityLogs, getMarketingOptInPage, updateMarketingOptInPage } from "api/bMarketingApi";
import { formatInternationalPhoneNumber } from "helpers/phoneFormatting";
import { useToast } from "../../context/ToastContext";
import { ToastAlert } from "components";
import SegmentsTab from "./SegmentsTab";
import TagsTab from "./TagsTab";

// ── Helpers ──────────────────────────────────────────────────────────────────

const SOURCE_LABELS = {
  guest_portal: "Guest Portal",
  sevenrooms: "SevenRooms",
  eat_app: "Eat App",
  opentable: "OpenTable",
  mailchimp: "Mailchimp",
  wix: "Wix",
  manual: "Manual",
  social_opt_in: "Social opt-in",
  Opentable: "OpenTable",
  Mailchimp: "Mailchimp",
  Wix: "Wix",
};

const SOURCE_COLORS = {
  guest_portal: "blue",
  sevenrooms: "green",
  eat_app: "jade",
  opentable: "orange",
  mailchimp: "yellow",
  wix: "violet",
  manual: "purple",
  social_opt_in: "cyan",
  Opentable: "orange",
  Mailchimp: "yellow",
  Wix: "violet",
};

const CONTACT_SOURCE_OPTIONS = [
  { value: "guest_portal", label: "Guest Portal" },
  { value: "sevenrooms", label: "SevenRooms" },
  { value: "eat_app", label: "Eat App" },
  { value: "opentable", label: "OpenTable" },
  { value: "mailchimp", label: "Mailchimp" },
  { value: "wix", label: "Wix" },
  { value: "manual", label: "Manual" },
  { value: "social_opt_in", label: "Social opt-in" },
];

const CONTACT_IMPORT_SOURCE_OPTIONS = [
  { value: "manual", label: "Manual CSV" },
  { value: "mailchimp", label: "Mailchimp" },
  { value: "sevenrooms", label: "SevenRooms" },
  { value: "opentable", label: "OpenTable" },
  { value: "wix", label: "Wix" },
];

const MANUAL_CSV_SAMPLE = [
  ["Full Name", "First Name", "Last Name", "Email", "Phone Number", "Date of Birth", "Gender", "Age Group", "Visits", "First Visit", "Last Visit", "Email Marketing Opt-in", "SMS Marketing Opt-in", "WhatsApp Marketing Opt-in", "Tags"],
  ["John Doe", "John", "Doe", "john.doe@example.com", "+447700900123", "1988-04-12", "male", "35-44", "24", "18-11-2025 09:15:30", "12-05-2026 17:42:10", "subscribed", "TRUE", "TRUE", "VIP; Regulars"],
].map((row) => row.join(",")).join("\n");

const MANUAL_CSV_SAMPLE_HREF = `data:text/csv;charset=utf-8,${encodeURIComponent(MANUAL_CSV_SAMPLE)}`;

const GENDER_LABELS = {
  male: "Male",
  female: "Female",
  other: "Other",
  unknown: "Unknown",
  no_response: "No response",
};

const AGE_GROUP_LABELS = {
  under_18: "Under 18",
  "18_24": "18-24",
  "25_34": "25-34",
  "35_44": "35-44",
  "45_54": "45-54",
  "55_64": "55-64",
  "65_plus": "65+",
  no_response: "No response",
};

const EMAIL_ACTIVITY_LABELS = {
  queued: "Queued",
  sent: "Sent",
  processed: "Processed",
  delivered: "Delivered",
  open: "Opened",
  click: "Clicked",
  bounce: "Bounced",
  spam_report: "Spam reported",
  subscribe: "Resubscribed",
  unsubscribe: "Unsubscribed",
  failed: "Failed",
  canceled: "Canceled",
};

const EMAIL_ACTIVITY_COLORS = {
  queued: "amber",
  sent: "blue",
  processed: "blue",
  delivered: "green",
  open: "cyan",
  click: "purple",
  bounce: "red",
  spam_report: "orange",
  subscribe: "green",
  unsubscribe: "gray",
  failed: "red",
  canceled: "gray",
};

const EMAIL_VALIDATION_COLORS = {
  valid: "green",
  invalid: "red",
  risky: "amber",
  unknown: "orange",
  pending: "gray",
  processing: "blue",
};

export const EmailValidationBadge = ({ contact, showDetails = false }) => {
  if (contact?.email_is_placeholder) {
    return <Badge color="gray" variant="soft">No email</Badge>;
  }
  const validation = contact?.email_validation;
  if (!validation) {
    return <Badge color="gray" variant="soft">Validation unavailable</Badge>;
  }
  const evidence = [
    validation.reason_label,
    validation.is_catch_all && validation.reason_code !== "catch_all" ? "Catch-all domain" : null,
    validation.is_disposable && validation.reason_code !== "disposable" ? "Disposable address" : null,
    validation.is_role_account && validation.reason_code !== "role_account" ? "Role account" : null,
  ].filter(Boolean);
  const badge = (
    <Badge color={EMAIL_VALIDATION_COLORS[validation.status] || "gray"} variant="soft">
      {validation.status_label}
    </Badge>
  );
  if (showDetails) {
    return (
      <Flex direction="column" gap="1" align="start">
        {badge}
        {evidence.length > 0 && <Text size="1" color="gray">{evidence.join(" · ")}</Text>}
        {validation.last_validated_at && (
          <Text size="1" color="gray">Checked {formatFullDateTime(validation.last_validated_at)}</Text>
        )}
      </Flex>
    );
  }
  return evidence.length > 0 ? <Tooltip content={evidence.join(" · ")}>{badge}</Tooltip> : badge;
};

export const CONTACT_TABLE_COLUMNS = [
  { key: "name", label: "Name", width: "12%" },
  { key: "email", label: "Email", width: "16%" },
  { key: "email_validation", label: "Email validation", width: "12%" },
  { key: "phone", label: "Phone", width: "10%" },
  { key: "source", label: "Source", width: "8%" },
  { key: "email_marketing", label: "Email Consent", width: "9%" },
  { key: "sms_marketing", label: "SMS Consent", width: "9%" },
  { key: "whatsapp_marketing", label: "WhatsApp Consent", width: "10%" },
  { key: "tags", label: "Tags", width: "10%" },
  { key: "gender", label: "Gender", width: "7%" },
  { key: "age_group", label: "Age Group", width: "8%" },
  { key: "locations", label: "Locations", width: "11%" },
  { key: "total_visits", label: "Visits", width: "7%" },
  { key: "first_visit", label: "First Visit", width: "10%" },
  { key: "last_visit", label: "Last Visit", width: "10%" },
  { key: "created_at", label: "Created", width: "10%" },
  { key: "updated_at", label: "Updated", width: "10%" },
];

// const DEFAULT_VISIBLE_COLUMNS = CONTACT_TABLE_COLUMNS.map((column) => column.key);
export const DEFAULT_VISIBLE_COLUMNS = [
  "name",
  "email",
  "email_validation",
  "phone",
  "source",
  "email_marketing",
  "sms_marketing",
  "whatsapp_marketing",
  "locations",
];

const DETAIL_FIELD_STYLE = {
  gridColumn: "span 2",
  minWidth: 0,
  width: "100%",
  padding: "var(--space-3)",
  border: "1px solid var(--gray-a5)",
  borderRadius: "var(--radius-2)",
  boxSizing: "border-box",
};

const DETAIL_GRID_STYLE = {
  display: "grid",
  gridTemplateColumns: "repeat(6, minmax(0, 1fr))",
  gap: "var(--space-3)",
};

const DEFAULT_MANUAL_CONTACT = {
  first_name: "",
  last_name: "",
  email_address: "",
  phone_number: "",
  gender: "no_response",
  age_group: "no_response",
  dob: "",
  email_marketing: false,
  sms_marketing: false,
  whatsapp_marketing: false,
  source: "manual",
  location_ids: [],
  tag_ids: [],
};

const IMPORT_STATUS_LABELS = {
  pending: "Pending",
  processing: "Processing",
  completed: "Completed",
  failed: "Failed",
};

const IMPORT_STATUS_COLORS = {
  pending: "gray",
  processing: "blue",
  completed: "green",
  failed: "red",
};

const IMPORT_JOBS_PAGE_SIZE = 10;
const IMPORT_JOB_TABLE_COLUMNS = [
  { label: "File", width: "22%" },
  { label: "Source", width: "9%" },
  { label: "Status", width: "9%" },
  { label: "Rows", width: "7%" },
  { label: "New", width: "7%" },
  { label: "Updated", width: "8%" },
  { label: "Failed", width: "7%" },
  { label: "Started On", width: "11%" },
  { label: "Complete On", width: "11%" },
  { label: "Last error", width: "20%" },
];

const formatDateTime = (value) => {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  return date.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

const formatFullDateTime = (value) => {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  return date.toLocaleString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: true,
  });
};

const formatSevenRoomsSpend = (amount, currency) => {
  if (amount == null || amount === "") return "—";
  const numeric = Number(amount);
  if (!Number.isFinite(numeric)) return String(amount);
  const code = String(currency || "GBP").trim().toUpperCase() || "GBP";
  try {
    return new Intl.NumberFormat("en-GB", {
      style: "currency",
      currency: code,
      currencyDisplay: "symbol",
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    }).format(numeric);
  } catch {
    const formatted = numeric.toLocaleString("en-GB", {
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    });
    return `${formatted} ${code}`;
  }
};

const DetailField = ({ label, children, style }) => (
  <Flex gap="2" style={{ ...DETAIL_FIELD_STYLE, ...style }} align="center" justify="between">
    <Text size="2" weight="medium">{label}:</Text>
    <Box style={{ minWidth: 0 }}>{children}</Box>
  </Flex>
);

const handleKeyboardActivation = (event, action) => {
  if (event.key === " ") {
    event.preventDefault();
    action();
  } else if (event.key === "Enter") {
    action();
  }
};

// ── Truncated text with tooltip ─────────────────────────────────────────────

const TruncatedText = ({ children, size, weight, color }) => {
  const text = typeof children === "string" ? children : String(children ?? "");
  const [isTruncated, setIsTruncated] = useState(false);
  const containerRef = useRef(null);

  useEffect(() => {
    const check = () => {
      const el = containerRef.current;
      if (el) setIsTruncated(el.scrollWidth > el.clientWidth);
    };
    check();
    window.addEventListener("resize", check);
    return () => window.removeEventListener("resize", check);
  }, [text]);

  const textEl = (
    <Box
      as="div"
      ref={containerRef}
      style={{ width: "100%", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
    >
      <Text size={size} weight={weight} color={color}>{text}</Text>
    </Box>
  );

  return isTruncated ? <Tooltip content={text}>{textEl}</Tooltip> : textEl;
};

// ── Stat card (same style as Analytics) ─────────────────────────────────────

const StatCard = ({ label, value, onClick, hint }) => (
  <Card style={{ display: "flex", padding: "var(--space-3)", flexDirection: "column", 
    justifyContent: "space-between", alignItems: "flex-start", flex: "1 0 0", alignSelf: "stretch", 
    height: "100%", cursor: onClick ? "pointer" : "default" }} onClick={onClick}
    role={onClick ? "button" : undefined}
    tabIndex={onClick ? 0 : undefined}
    onKeyDown={onClick ? (event) => handleKeyboardActivation(event, onClick) : undefined}>
    <Flex justify="between" align="center" gap="2" style={{ alignSelf: "stretch" }}>
      <Flex align="center" gap="1">
        <Text size="2" weight="medium" color="gray">{label}</Text>
        {hint && <Tooltip content={hint}><InfoCircledIcon color="gray" /></Tooltip>}
      </Flex>
      <Text size="4" weight="bold">{value?.toLocaleString() ?? 0}</Text>
    </Flex>
  </Card>
);

// ── Consent badge ───────────────────────────────────────────────────────────

const ConsentBadge = ({ value }) => (
  <Badge color={value ? "green" : "gray"} variant="soft" size="1">
    {value ? <CheckCircledIcon width="12" height="12" /> : <CrossCircledIcon width="12" height="12" />}
    <Text size="1">{value ? "Opted-in" : "No"}</Text>
  </Badge>
);

// ── Tag badges ───────────────────────────────────────────────────────────────

const TagBadges = ({ tags, max = 2 }) => {
  if (!tags?.length) return <Text size="2" color="gray">—</Text>;

  const visible = tags.slice(0, max);
  const overflow = tags.length - visible.length;

  return (
    <Flex gap="1" align="center" style={{ overflow: "hidden" }}>
      {visible.map((tag) => (
        <Badge key={tag.tag_id} variant="soft" size="1" style={{ flexShrink: 0 }}>
          {tag.name}
        </Badge>
      ))}
      {overflow > 0 && (
        <Tooltip content={tags.map((tag) => tag.name).join(", ")}>
          <Badge variant="soft" color="gray" size="1" style={{ flexShrink: 0 }}>+{overflow}</Badge>
        </Tooltip>
      )}
    </Flex>
  );
};

// ═══════════════════════════════════════════════════════════════════════════
// Main Component
// ═══════════════════════════════════════════════════════════════════════════

export default function MarketingAudiences() {
  const { userProfile } = useProfileContext();
  const { locationsData } = useLocationContext();
  const { setToastMessage } = useToast();
  const orgId = userProfile?.organisation_id;

  // Ref for SegmentsTab actions
  const segmentsRef = useRef(null);

  // Ref for TagsTab actions
  const tagsRef = useRef(null);

  // Tab state
  const [activeTab, setActiveTab] = useState("contacts");

  // Contacts filters
  const [filters, setFilters] = useState({
    search: "",
    location_id: "",
    source: "",
    email_marketing: "",
    sms_marketing: "",
    whatsapp_marketing: "",
    email_validation_status: "",
    tag_id: "",
    page: 1,
    page_size: 25,
  });
  const [searchInput, setSearchInput] = useState("");
  const [visibleColumns, setVisibleColumns] = useState(DEFAULT_VISIBLE_COLUMNS);
  const [isAddContactsOpen, setIsAddContactsOpen] = useState(false);
  const [addContactsMode, setAddContactsMode] = useState("choice");
  const [csvFile, setCsvFile] = useState(null);
  const [csvSource, setCsvSource] = useState("manual");
  const [csvLocationId, setCsvLocationId] = useState("none");
  const [manualContact, setManualContact] = useState(DEFAULT_MANUAL_CONTACT);
  const [selectedContact, setSelectedContact] = useState(null);
  const [contactDetails, setContactDetails] = useState(null);
  const [contactEmailActivity, setContactEmailActivity] = useState([]);
  const [contactEmailActivityCount, setContactEmailActivityCount] = useState(0);
  const [isContactDetailsOpen, setIsContactDetailsOpen] = useState(false);
  const [isContactDetailsLoading, setIsContactDetailsLoading] = useState(false);
  const [importJobsPage, setImportJobsPage] = useState(1);
  const [isSuppressionsOpen, setIsSuppressionsOpen] = useState(false);
  const [isOptInLinkOpen, setIsOptInLinkOpen] = useState(false);
  const [optInLink, setOptInLink] = useState("");
  const [optInLinkLoading, setOptInLinkLoading] = useState(false);
  const [optInPageActive, setOptInPageActive] = useState(true);
  const [optInPageSaving, setOptInPageSaving] = useState(false);
  const [suppressionFilters, setSuppressionFilters] = useState({ active: "true", reason: "", search: "", page: 1, page_size: 25 });
  const [suppressionToClear, setSuppressionToClear] = useState(null);
  const [suppressionClearNote, setSuppressionClearNote] = useState("");
  const [suppressionRiskAcknowledged, setSuppressionRiskAcknowledged] = useState(false);

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => {
      setFilters((prev) => ({ ...prev, search: searchInput, page: 1 }));
    }, 400);
    return () => clearTimeout(timer);
  }, [searchInput]);

  useEffect(() => {
    setOptInLink("");
    setOptInPageActive(true);
  }, [orgId]);

  // Queries
  const { contacts, pagination, isLoading, refetch } = useContacts(orgId, filters);
  const { stats, isLoading: statsLoading } = useContactsStats(orgId);
  const {
    suppressions,
    pagination: suppressionPagination,
    canManage: canManageSuppressions,
    isLoading: suppressionsLoading,
  } = useEmailSuppressions(orgId, suppressionFilters, isSuppressionsOpen);
  const { contactImports, isLoading: importsLoading, refetch: refetchImports } = useContactImports(orgId);
  const { contactTags } = useContactTags(orgId);
  const deleteContactMutation = useDeleteContact();
  const createContactMutation = useCreateContact();
  const importContactsMutation = useImportContactsCsv();
  const updateContactMutation = useUpdateContact();
  const clearSuppressionMutation = useClearEmailSuppression();

  // Delete state
  const [contactToDelete, setContactToDelete] = useState(null);

  const handleDelete = async () => {
    if (!contactToDelete) return;
    try {
      await deleteContactMutation.mutateAsync(contactToDelete.contact_id);
      setToastMessage("Contact deleted");
      setIsContactDetailsOpen(false);
      setSelectedContact(null);
      setContactDetails(null);
    } catch {
      setToastMessage("Failed to delete contact");
    }
    setContactToDelete(null);
  };

  const resetSuppressionClear = () => {
    setSuppressionToClear(null);
    setSuppressionClearNote("");
    setSuppressionRiskAcknowledged(false);
  };

  const handleClearSuppression = async () => {
    if (!suppressionToClear || !orgId) return;
    try {
      await clearSuppressionMutation.mutateAsync({
        suppressionId: suppressionToClear.id,
        data: {
          organisation_id: orgId,
          note: suppressionClearNote.trim(),
          acknowledge_risk: suppressionRiskAcknowledged,
        },
      });
      setToastMessage("Suppression cleared");
      resetSuppressionClear();
    } catch (error) {
      setToastMessage(error.response?.data?.error || "Failed to clear suppression");
    }
  };

  // Filter helpers
  const handleFilterChange = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value, page: 1 }));
  };

  const handlePageChange = (newPage) => {
    setFilters((prev) => ({ ...prev, page: newPage }));
  };

  // Location options
  const locationOptions = useMemo(() => {
    if (!locationsData) return [];
    return locationsData.map((l) => ({ id: l.id, name: l.name }));
  }, [locationsData]);

  // Location list for segments (needs both id and name)
  const locationsList = useMemo(() => {
    if (!locationsData) return [];
    return locationsData.map((l) => ({ id: l.id, name: l.name }));
  }, [locationsData]);

  const loading = isLoading || statsLoading;
  const visibleColumnSet = useMemo(() => new Set(visibleColumns), [visibleColumns]);
  const activeImportCount = contactImports.filter((job) => ["pending", "processing"].includes(job.status)).length;
  const importJobsTotalPages = Math.max(1, Math.ceil(contactImports.length / IMPORT_JOBS_PAGE_SIZE));
  const paginatedContactImports = useMemo(() => {
    const start = (importJobsPage - 1) * IMPORT_JOBS_PAGE_SIZE;
    return contactImports.slice(start, start + IMPORT_JOBS_PAGE_SIZE);
  }, [contactImports, importJobsPage]);

  useEffect(() => {
    setImportJobsPage((page) => Math.min(page, importJobsTotalPages));
  }, [importJobsTotalPages]);

  const isColumnVisible = (columnKey) => visibleColumnSet.has(columnKey);

  const toggleAllColumns = () => {
    setVisibleColumns((prev) => (
      prev.length === CONTACT_TABLE_COLUMNS.length ? [] : CONTACT_TABLE_COLUMNS.map((column) => column.key)
    ));
  };

  const resetAddContactsFlow = () => {
    setAddContactsMode("choice");
    setCsvFile(null);
    setCsvSource("manual");
    setCsvLocationId("none");
    setManualContact(DEFAULT_MANUAL_CONTACT);
  };

  const openAddContactsDialog = () => {
    resetAddContactsFlow();
    setIsAddContactsOpen(true);
  };

  const updateManualContact = (key, value) => {
    setManualContact((prev) => ({ ...prev, [key]: value }));
  };

  const openContactDetails = async (contact) => {
    setSelectedContact(contact);
    setContactDetails(null);
    setContactEmailActivity([]);
    setContactEmailActivityCount(0);
    setIsContactDetailsOpen(true);
    setIsContactDetailsLoading(true);

    try {
      const [detailResult, activityResult] = await Promise.allSettled([
        getContactDetail(contact.contact_id),
        getEmailActivityLogs(orgId, {
          contact_id: contact.contact_id,
          channel: "email",
          page: 1,
          page_size: 100,
        }),
      ]);

      if (detailResult.status === "rejected") {
        throw detailResult.reason;
      }

      setContactDetails(detailResult.value.data);
      if (activityResult.status === "fulfilled") {
        setContactEmailActivity(activityResult.value.data.results || []);
        setContactEmailActivityCount(activityResult.value.data.count || 0);
      } else {
        setToastMessage("Contact loaded, but email activity could not be loaded");
      }
    } catch (error) {
      setToastMessage("Failed to load contact details");
    } finally {
      setIsContactDetailsLoading(false);
    }
  };

  const handleImportContacts = async () => {
    if (!orgId) {
      setToastMessage("Organisation is not ready yet");
      return;
    }

    if (!csvFile) {
      setToastMessage("Please choose a CSV file to import");
      return;
    }

    try {
      await importContactsMutation.mutateAsync({
        organisationId: orgId,
        source: csvSource,
        file: csvFile,
        defaultLocationId: csvLocationId,
        emailMarketingStatus: "unknown",
      });
      setToastMessage("Contact import started");
      refetchImports();
      setIsAddContactsOpen(false);
      resetAddContactsFlow();
    } catch (error) {
      setToastMessage(error.response?.data?.error || "Failed to start contact import");
    }
  };

  const handleContactTagsChange = async (tagId, checked) => {
    const contact = contactDetails || selectedContact;
    if (!contact?.contact_id) return;

    const currentTagIds = (contact.tags || []).map((tag) => tag.tag_id);
    const nextTagIds = checked
      ? [...currentTagIds, tagId]
      : currentTagIds.filter((id) => id !== tagId);

    try {
      const response = await updateContactMutation.mutateAsync({
        contactId: contact.contact_id,
        data: { tag_ids: nextTagIds },
      });
      setContactDetails((prev) => (prev ? { ...prev, tags: response.data.tags } : prev));
      setSelectedContact((prev) => (prev ? { ...prev, tags: response.data.tags } : prev));
    } catch {
      setToastMessage("Failed to update contact tags");
    }
  };

  const handleManualContactSubmit = async () => {
    if (!orgId) {
      setToastMessage("Organisation is not ready yet");
      return;
    }

    const hasEmail = manualContact.email_address.trim();
    const hasPhone = manualContact.phone_number.trim();

    if (!hasEmail && !hasPhone) {
      setToastMessage("Add an email address or phone number");
      return;
    }

    try {
      await createContactMutation.mutateAsync({
        ...manualContact,
        organisation_id: orgId,
        dob: manualContact.dob || null,
      });
      setToastMessage("Contact saved");
      setIsAddContactsOpen(false);
      resetAddContactsFlow();
    } catch (error) {
      const errorMessage = error.response?.data?.error || error.response?.data?.non_field_errors?.[0] || "Failed to save contact";
      setToastMessage(errorMessage);
    }
  };

  const renderContactCell = (contact, columnKey) => {
    switch (columnKey) {
      case "name":
        return (
          <TruncatedText weight="regular" size="2">
            {contact.full_name || "—"}
          </TruncatedText>
        );
      case "email":
        return (
          <TruncatedText weight="regular" size="2" color="gray">
            {contact.email_is_placeholder ? "No email" : contact.email_address}
          </TruncatedText>
        );
      case "email_validation":
        return <EmailValidationBadge contact={contact} />;
      case "phone":
        return <TruncatedText weight="regular" size="2" color="gray">{formatInternationalPhoneNumber(contact.phone_number) || "—"}</TruncatedText>;
      case "source":
        return (
          <Badge color={SOURCE_COLORS[contact.source] || "gray"} variant="soft" size="1">
            {SOURCE_LABELS[contact.source] || contact.source || "—"}
          </Badge>
        );
      case "email_marketing":
        return <ConsentBadge value={contact.email_marketing} />;
      case "sms_marketing":
        return <ConsentBadge value={contact.sms_marketing} />;
      case "whatsapp_marketing":
        return <ConsentBadge value={contact.whatsapp_marketing} />;
      case "tags":
        return <TagBadges tags={contact.tags} />;
      case "gender":
        return (
          <TruncatedText weight="regular" size="2" color="gray">
            {GENDER_LABELS[contact.gender] || contact.gender || "—"}
          </TruncatedText>
        );
      case "age_group":
        return (
          <TruncatedText weight="regular" size="2" color="gray">
            {AGE_GROUP_LABELS[contact.age_group] || contact.age_group || "—"}
          </TruncatedText>
        );
      case "locations":
        return (
          <TruncatedText weight="regular" size="2" color="gray">
            {contact.location_names?.length ? contact.location_names.join(", ") : "—"}
          </TruncatedText>
        );
      case "total_visits":
        return <TruncatedText weight="regular" size="2" color="gray">{contact.total_visits ?? 0}</TruncatedText>;
      case "first_visit":
        return <TruncatedText weight="regular" size="2" color="gray">{formatDateTime(contact.first_visit)}</TruncatedText>;
      case "last_visit":
        return <TruncatedText weight="regular" size="2" color="gray">{formatDateTime(contact.last_visit)}</TruncatedText>;
      case "created_at":
        return <TruncatedText weight="regular" size="2" color="gray">{formatDateTime(contact.created_at)}</TruncatedText>;
      case "updated_at":
        return <TruncatedText weight="regular" size="2" color="gray">{formatDateTime(contact.updated_at)}</TruncatedText>;
      default:
        return null;
    }
  };

  return (
    <>
      {/* Global progress bar */}
      {loading && (
        <div style={{ position: "fixed", top: 0, left: 0, width: "100%", zIndex: 1000 }}>
          <Progress size="1" duration="1" />
        </div>
      )}

      <Flex direction="column" gap="5" style={{ alignSelf: "stretch", alignItems: "flex-start", flex: "1 0 0", width: "100%" }}>
        {/* Tab bar */}
        <Flex justify="between" align="center" style={{ alignSelf: "stretch" }}>
          <SegmentedControl.Root value={activeTab} onValueChange={setActiveTab}>
            <SegmentedControl.Item value="contacts">
              <Flex gap="1" align="center">
                <PersonIcon />
                <Text>Contacts</Text>
              </Flex>
            </SegmentedControl.Item>
            <SegmentedControl.Item value="segments">
              <Flex gap="1" align="center">
                <PieChartIcon />
                <Text>Segments</Text>
              </Flex>
            </SegmentedControl.Item>
            <SegmentedControl.Item value="tags">
              <Flex gap="1" align="center">
                <BookmarkIcon />
                <Text>Tags</Text>
              </Flex>
            </SegmentedControl.Item>
            {/* <SegmentedControl.Item value="labels">
              <Flex gap="1" align="center">
                <BookmarkFilledIcon />
                <Text>Labels</Text>
              </Flex>
            </SegmentedControl.Item> */}
          </SegmentedControl.Root>

          {activeTab === "contacts" && (
            <Flex gap="2" align="center">
              <Dialog.Root open={isOptInLinkOpen} onOpenChange={async open => {
                setIsOptInLinkOpen(open);
                if (open && !optInLink) {
                  setOptInLinkLoading(true);
                  try {
                    const response = await getMarketingOptInPage(orgId);
                    setOptInLink(`${window.location.origin}${response.data.path}`);
                    setOptInPageActive(response.data.is_active);
                  } catch (error) {
                    setToastMessage(
                      error.response?.data?.message
                      || error.response?.data?.detail
                      || error.response?.data?.error
                      || error.message
                      || "Couldn’t create the opt-in link"
                    );
                  }
                  finally { setOptInLinkLoading(false); }
                }
              }}>
                <Dialog.Trigger><Button variant="soft"><Link2Icon />Share opt-in link</Button></Dialog.Trigger>
                <Dialog.Content maxWidth="560px">
                  <Dialog.Title>Share your newsletter sign-up link</Dialog.Title>
                  <Dialog.Description>Use this URL in Instagram, social profiles, messages, or your website.</Dialog.Description>
                  <Flex direction="column" gap="3" mt="4">
                    <Card>
                      <Flex justify="between" align="center" gap="4">
                        <Box>
                          <Flex align="center" gap="2">
                            <Text size="2" weight="bold">Public sign-up page</Text>
                            <Badge color={optInPageActive ? "green" : "gray"} variant="soft">
                              {optInPageActive ? "Active" : "Paused"}
                            </Badge>
                          </Flex>
                          <Text as="p" size="1" color="gray" mt="1">
                            {optInPageActive
                              ? "Anyone with the link can subscribe."
                              : "The link is paused and cannot accept subscriptions."}
                          </Text>
                        </Box>
                        <Switch
                          checked={optInPageActive}
                          disabled={optInLinkLoading || optInPageSaving || !optInLink}
                          aria-label="Enable public newsletter sign-up page"
                          onCheckedChange={async checked => {
                            setOptInPageSaving(true);
                            try {
                              const response = await updateMarketingOptInPage(orgId, { is_active: checked });
                              setOptInPageActive(response.data.is_active);
                              setToastMessage(response.data.is_active ? "Opt-in page enabled" : "Opt-in page paused");
                            } catch (error) {
                              setToastMessage(
                                error.response?.data?.error
                                || error.response?.data?.detail
                                || error.message
                                || "Couldn’t update the opt-in page"
                              );
                            } finally {
                              setOptInPageSaving(false);
                            }
                          }}
                        />
                      </Flex>
                    </Card>
                    <TextField.Root value={optInLink} readOnly placeholder={optInLinkLoading ? "Creating link…" : ""} />
                    <Button disabled={!optInLink || !optInPageActive} onClick={async () => { await navigator.clipboard.writeText(optInLink); setToastMessage("Opt-in link copied"); }}><CopyIcon />Copy link</Button>
                    <Text size="1" color="gray">Subscribers are added to your marketing audience with email consent recorded.</Text>
                  </Flex>
                </Dialog.Content>
              </Dialog.Root>
              <Button variant="solid" onClick={openAddContactsDialog}>
                <PlusIcon />
                Add contacts
              </Button>
              <Dialog.Root>
                <Dialog.Trigger>
                  <Button variant="soft" color="gray">
                    Imported jobs
                    {activeImportCount > 0 && <Badge color="blue" size="1" variant="solid">{activeImportCount}</Badge>}
                  </Button>
                </Dialog.Trigger>
                <Dialog.Content
                  style={{
                    width: "calc(100vw - 48px)",
                    height: "min(760px, calc(100vh - 96px))",
                    maxWidth: "none",
                    boxSizing: "border-box",
                    display: "flex",
                    flexDirection: "column",
                    overflow: "hidden",
                  }}
                >
                  <Flex justify="between" align="start" gap="4" mb="4" style={{ flex: "0 0 auto" }}>
                    <Box>
                      <Dialog.Title>Contact imports</Dialog.Title>
                      <Dialog.Description size="2" color="gray">
                        Review recent CSV imports, progress, totals, and row errors.
                      </Dialog.Description>
                    </Box>
                    <Flex gap="3" align="center">
                      <Button variant="soft" color="gray" onClick={() => refetchImports()}>
                        <ReloadIcon />
                        Refresh
                      </Button>
                      <Dialog.Close>
                        <Button variant="soft" color="gray">Close</Button>
                      </Dialog.Close>
                    </Flex>
                  </Flex>

                  <Box
                    style={{
                      flex: "1 1 auto",
                      minHeight: 0,
                      maxWidth: "100%",
                      overflow: "auto",
                      border: "1px solid var(--gray-a6)",
                      borderRadius: "var(--radius-4)",
                    }}
                  >
                    {importsLoading ? (
                      <Flex align="center" justify="center" style={{ height: "100%" }}>
                        <Text size="2" color="gray">Loading imports...</Text>
                      </Flex>
                    ) : contactImports.length === 0 ? (
                      <Flex align="center" justify="center" style={{ height: "100%" }}>
                        <Text size="2" color="gray">No imports yet</Text>
                      </Flex>
                    ) : (
                      <Table.Root layout="fixed" style={{ minWidth: 1120, width: "100%", height: "100%" }}>
                        <Table.Header style={{ position: "sticky", top: 0, zIndex: 1, backgroundColor: "var(--color-panel-solid)" }}>
                          <Table.Row>
                            {IMPORT_JOB_TABLE_COLUMNS.map((column) => (
                              <Table.ColumnHeaderCell key={column.label} style={{ width: column.width }}>
                                {column.label}
                              </Table.ColumnHeaderCell>
                            ))}
                          </Table.Row>
                        </Table.Header>
                        <Table.Body>
                          {paginatedContactImports.map((job) => {
                            const processed = job.processed_rows || 0;
                            const total = job.total_rows || 0;
                            const progressLabel = total > 0 ? `${processed}/${total}` : `${processed}`;
                            const firstError = job.error_summary?.[0];
                            const errorLabel = job.last_error || (firstError ? `Row ${firstError.row}: ${firstError.error}` : "—");

                            return (
                              <Table.Row key={job.import_id} align="center">
                                <Table.Cell style={{ maxWidth: 280 }}>
                                  <TruncatedText size="2" weight="medium">
                                    {job.file_name || "Contact import"}
                                  </TruncatedText>
                                </Table.Cell>
                                <Table.Cell>
                                  <Badge color={SOURCE_COLORS[job.source] || "gray"} variant="soft" size="1">
                                    {SOURCE_LABELS[job.source] || job.source || "—"}
                                  </Badge>
                                </Table.Cell>
                                <Table.Cell>
                                  <Badge color={IMPORT_STATUS_COLORS[job.status] || "gray"} variant="soft" size="1">
                                    {IMPORT_STATUS_LABELS[job.status] || job.status}
                                  </Badge>
                                </Table.Cell>
                                <Table.Cell>{progressLabel}</Table.Cell>
                                <Table.Cell>{job.imported_count || 0}</Table.Cell>
                                <Table.Cell>{job.updated_count || 0}</Table.Cell>
                                <Table.Cell>
                                  <Badge color={(job.failed_count || 0) > 0 ? "red" : "gray"} variant="soft" size="1">
                                    {job.failed_count || 0}
                                  </Badge>
                                </Table.Cell>
                                <Table.Cell>{formatFullDateTime(job.started_at || job.created_at)}</Table.Cell>
                                <Table.Cell>{formatFullDateTime(job.completed_at)}</Table.Cell>
                                <Table.Cell style={{ maxWidth: 360 }}>
                                  <TruncatedText size="2" color={(job.last_error || firstError) ? "red" : "gray"}>
                                    {errorLabel}
                                  </TruncatedText>
                                </Table.Cell>
                              </Table.Row>
                            );
                          })}
                        </Table.Body>
                      </Table.Root>
                    )}
                  </Box>
                  {!importsLoading && contactImports.length > 0 && (
                    <Flex
                      justify="between"
                      align="center"
                      gap="3"
                      pt="3"
                      mt="3"
                      style={{ flex: "0 0 auto" }}>
                      <Text size="2" color="gray">
                        Showing {(importJobsPage - 1) * IMPORT_JOBS_PAGE_SIZE + 1}-
                        {Math.min(importJobsPage * IMPORT_JOBS_PAGE_SIZE, contactImports.length)} of {contactImports.length} imports
                      </Text>
                      <Flex gap="2" align="center">
                        <Button
                          variant="soft"
                          color="gray"
                          disabled={importJobsPage <= 1}
                          onClick={() => setImportJobsPage((page) => Math.max(1, page - 1))}
                        >
                          <ChevronLeftIcon />
                          Previous
                        </Button>
                        <Text size="2">Page {importJobsPage} of {importJobsTotalPages}</Text>
                        <Button
                          variant="soft"
                          color="gray"
                          disabled={importJobsPage >= importJobsTotalPages}
                          onClick={() => setImportJobsPage((page) => Math.min(importJobsTotalPages, page + 1))}
                        >
                          Next
                          <ChevronRightIcon />
                        </Button>
                      </Flex>
                    </Flex>
                  )}
                </Dialog.Content>
              </Dialog.Root>
            </Flex>
          )}

          {/* Segment action buttons (shown only when segments tab is active) */}
          {activeTab === "segments" && (
            <Flex gap="2">
              <Tooltip content="Create a new segment">
                <Button variant="solid" onClick={() => segmentsRef.current?.create()}>
                  <PlusIcon /> Create segment
                </Button>
              </Tooltip>
              <Tooltip content="Refresh the segments list">
                <Button variant="soft" color="gray" onClick={() => segmentsRef.current?.refresh()}>
                  <ReloadIcon />
                </Button>
              </Tooltip>
            </Flex>
          )}

          {/* Tag action buttons (shown only when tags tab is active) */}
          {activeTab === "tags" && (
            <Flex gap="2">
              <Tooltip content="Create a new tag">
                <Button variant="solid" onClick={() => tagsRef.current?.create()}>
                  <PlusIcon /> Create tag
                </Button>
              </Tooltip>
              <Tooltip content="Refresh the tags list">
                <Button variant="soft" color="gray" onClick={() => tagsRef.current?.refresh()}>
                  <ReloadIcon />
                </Button>
              </Tooltip>
            </Flex>
          )}
        </Flex>

        {/* Segments tab */}
        {activeTab === "segments" && (
          <SegmentsTab ref={segmentsRef} orgId={orgId} locations={locationsList} setToastMessage={setToastMessage} />
        )}

        {/* Tags tab */}
        {activeTab === "tags" && (
          <TagsTab ref={tagsRef} orgId={orgId} setToastMessage={setToastMessage} />
        )}

        {activeTab === "labels" && (
          <Flex direction="column" style={{ justifyContent: "center", alignItems: "center", alignSelf: "stretch",
            border: "1px dashed var(--gray-a6)", borderRadius: "var(--radius-4)", height: "100%", flex: "1 0 0",
            padding: "var(--space-6)" }}>
            <Text color="gray">Labels coming soon.</Text>
          </Flex>
        )}

        {/* Contacts tab */}
        {activeTab === "contacts" && (
          <>
            {/* Stat cards */}
            <Flex gap="3" wrap="wrap" style={{ alignSelf: "stretch" }}>
              <StatCard label="Total Contacts" value={stats.total_contacts} />
              <StatCard label="Email Opted-in" value={stats.email_opted_in} />
              <StatCard label="SMS Opted-in" value={stats.sms_opted_in} />
              <StatCard label="WhatsApp Opted-in" value={stats.whatsapp_opted_in} />
              <StatCard label="Sources" value={Object.keys(stats.by_source || {}).length} />
              <StatCard label="Email Suppressed" value={stats.email_suppressed} 
                hint="Open suppression management"
                onClick={() => setIsSuppressionsOpen(true)}
              />
            </Flex>

            {/* Filters */}
            <Flex gap="3" align="center" wrap="wrap" justify="between" style={{ alignSelf: "stretch" }}>
              <Flex gap="3" align="center" wrap="wrap">
                {/* Search */}
                  <TextField.Root
                    placeholder="Search contact..."
                    value={searchInput}
                    onChange={(e) => setSearchInput(e.target.value)}
                    style={{ width: "200px" }}
                  >
                    <TextField.Slot>
                      <MagnifyingGlassIcon />
                    </TextField.Slot>
                  </TextField.Root>

                {/* Location filter */}
                <Select.Root
                  value={filters.location_id || "all"}
                  onValueChange={(v) => handleFilterChange("location_id", v === "all" ? "" : v)}
                >
                  <Select.Trigger placeholder="All Locations" style={{ minWidth: "130px" }} />
                  <Select.Content>
                    <Select.Item value="all">All Locations</Select.Item>
                    {locationOptions.map((loc) => (
                      <Select.Item key={loc.id} value={String(loc.id)}>{loc.name}</Select.Item>
                    ))}
                  </Select.Content>
                </Select.Root>

                {/* Source filter */}
                <Select.Root
                  value={filters.source || "all"}
                  onValueChange={(v) => handleFilterChange("source", v === "all" ? "" : v)}
                >
                  <Select.Trigger placeholder="All Sources" style={{ minWidth: "130px" }} />
                  <Select.Content>
                    <Select.Item value="all">All Sources</Select.Item>
                    {CONTACT_SOURCE_OPTIONS.map((source) => (
                      <Select.Item key={source.value} value={source.value}>{source.label}</Select.Item>
                    ))}
                  </Select.Content>
                </Select.Root>

                {/* Email consent filter */}
                <Select.Root
                  value={filters.email_marketing === "" ? "all" : String(filters.email_marketing)}
                  onValueChange={(v) => handleFilterChange("email_marketing", v === "all" ? "" : v)}
                >
                  <Select.Trigger placeholder="Email Consent" style={{ minWidth: "140px" }} />
                  <Select.Content>
                    <Select.Item value="all">Email Consent</Select.Item>
                    <Select.Item value="true">Opted-in</Select.Item>
                    <Select.Item value="false">Not opted-in</Select.Item>
                  </Select.Content>
                </Select.Root>

                <Select.Root
                  value={filters.email_validation_status || "all"}
                  onValueChange={(v) => handleFilterChange("email_validation_status", v === "all" ? "" : v)}
                >
                  <Select.Trigger placeholder="Email validation" style={{ minWidth: "155px" }} />
                  <Select.Content>
                    <Select.Item value="all">Email validation</Select.Item>
                    <Select.Item value="valid">Valid</Select.Item>
                    <Select.Item value="invalid">Invalid</Select.Item>
                    <Select.Item value="risky">Risky</Select.Item>
                    <Select.Item value="unknown">Unknown</Select.Item>
                    <Select.Item value="pending">Pending</Select.Item>
                    <Select.Item value="processing">Checking</Select.Item>
                    <Select.Item value="no_email">No email</Select.Item>
                    <Select.Item value="missing_state">Validation unavailable</Select.Item>
                  </Select.Content>
                </Select.Root>

                {/* SMS consent filter */}
                <Select.Root
                  value={filters.sms_marketing === "" ? "all" : String(filters.sms_marketing)}
                  onValueChange={(v) => handleFilterChange("sms_marketing", v === "all" ? "" : v)}
                >
                  <Select.Trigger placeholder="SMS Consent" style={{ minWidth: "140px" }} />
                  <Select.Content>
                    <Select.Item value="all">SMS Consent</Select.Item>
                    <Select.Item value="true">Opted-in</Select.Item>
                    <Select.Item value="false">Not opted-in</Select.Item>
                  </Select.Content>
                </Select.Root>

                {/* WhatsApp consent filter */}
                <Select.Root
                  value={filters.whatsapp_marketing === "" ? "all" : String(filters.whatsapp_marketing)}
                  onValueChange={(v) => handleFilterChange("whatsapp_marketing", v === "all" ? "" : v)}
                >
                  <Select.Trigger placeholder="WhatsApp Consent" style={{ minWidth: "165px" }} />
                  <Select.Content>
                    <Select.Item value="all">WhatsApp Consent</Select.Item>
                    <Select.Item value="true">Opted-in</Select.Item>
                    <Select.Item value="false">Not opted-in</Select.Item>
                  </Select.Content>
                </Select.Root>

                {/* Tag filter */}
                <Select.Root
                  value={filters.tag_id || "all"}
                  onValueChange={(v) => handleFilterChange("tag_id", v === "all" ? "" : v)}
                >
                  <Select.Trigger placeholder="All Tags" style={{ minWidth: "120px" }} />
                  <Select.Content>
                    <Select.Item value="all">All Tags</Select.Item>
                    {contactTags.map((tag) => (
                      <Select.Item key={tag.tag_id} value={tag.tag_id}>{tag.name}</Select.Item>
                    ))}
                  </Select.Content>
                </Select.Root>
              </Flex>
              
              {/* Columns filter */}
              <Flex gap="2" style={{ justifyContent: "flex-end", alignItems: "center", width: "100%" }}>
                <Popover.Root>
                  <Popover.Trigger asChild>
                    <Button variant="soft" color="gray">
                      <MixerHorizontalIcon />
                      Columns
                    </Button>
                  </Popover.Trigger>
                  <Popover.Content maxHeight="320px" style={{ width: "220px", padding: 0 }}>
                    <Flex direction="column" gap="1" p="2">
                      <Flex p="2" align="center" gap="2">
                        <Checkbox
                          size="2"
                          checked={visibleColumns.length === CONTACT_TABLE_COLUMNS.length}
                          onCheckedChange={toggleAllColumns}
                        />
                        <Text size="2">Select all</Text>
                      </Flex>
                      <CheckboxGroup.Root
                        size="2"
                        value={visibleColumns}
                        onValueChange={setVisibleColumns}
                        style={{ display: "flex", flexDirection: "column", gap: "var(--space-1)" }}
                      >
                        {CONTACT_TABLE_COLUMNS.map((column) => (
                          <Flex key={column.key} p="2">
                            <CheckboxGroup.Item value={column.key}>{column.label}</CheckboxGroup.Item>
                          </Flex>
                        ))}
                      </CheckboxGroup.Root>
                    </Flex>
                  </Popover.Content>
                </Popover.Root>
                <Tooltip content="Refresh contacts list">
                  <Button variant="soft" color="gray" 
                    onClick={() => { refetch(); setToastMessage("Contacts refreshed"); }}>
                    <ReloadIcon />
                  </Button>
                </Tooltip>
              </Flex>
            </Flex>

            {/* Contacts table */}
            <Card style={{ display: "flex", alignSelf: "stretch", flexDirection: "column", alignItems: "flex-start",
              flex: "1 0 0", height: "100%", maxWidth: "100%", overflow: "auto" }}>
              {contacts.length === 0 ? (
                <Flex justify="center" align="center" style={{ width: "100%", height: "100%" }}>
                  <Text color="gray">{isLoading ? "Loading contacts…" : "No contacts found"}</Text>
                </Flex>
              ) : (
                <Table.Root layout="fixed" style={{ width: "100%", height: "100%" }}>
                  <Table.Header style={{ position: "sticky", top: 0, zIndex: 1, backgroundColor: "var(--color-panel-solid)" }}>
                    <Table.Row align="center">
                      {CONTACT_TABLE_COLUMNS.filter((column) => isColumnVisible(column.key)).map((column) => (
                        <Table.ColumnHeaderCell key={column.key} style={{ width: column.width }}>
                          <TruncatedText size="2" weight="medium">{column.label}</TruncatedText>
                        </Table.ColumnHeaderCell>
                      ))}
                      <Table.ColumnHeaderCell style={{ width: "4%" }} />
                    </Table.Row>
                  </Table.Header>
                  <Table.Body>
                    {contacts.map((c) => (
                      <Table.Row key={c.id}>
                        {CONTACT_TABLE_COLUMNS.filter((column) => isColumnVisible(column.key)).map((column) => (
                          <Table.Cell key={column.key}>{renderContactCell(c, column.key)}</Table.Cell>
                        ))}
                        <Table.Cell>
                          <Tooltip content="View contact details">
                            <Button
                              variant="soft"
                              color="gray"
                              size="1"
                              onClick={() => openContactDetails(c)}
                            >
                              <InfoCircledIcon />
                            </Button>
                          </Tooltip>
                        </Table.Cell>
                      </Table.Row>
                    ))}
                  </Table.Body>
                </Table.Root>
              )}
            </Card>

            {/* Pagination */}
            {pagination.numPages > 1 && (
              <Flex justify="between" align="center" style={{ alignSelf: "stretch" }}>
                <Text size="2" color="gray">
                  Showing {(pagination.currentPage - 1) * filters.page_size + 1} –{" "}
                  {Math.min(pagination.currentPage * filters.page_size, pagination.count)} of{" "}
                  {pagination.count} contacts
                </Text>
                <Flex gap="2" align="center">
                  <Button
                    variant="soft"
                    disabled={pagination.currentPage <= 1}
                    onClick={() => handlePageChange(pagination.currentPage - 1)}
                  >
                    <ChevronLeftIcon />
                    Previous
                  </Button>
                  <Text size="2">Page {pagination.currentPage} of {pagination.numPages}</Text>
                  <Button
                    variant="soft"
                    disabled={pagination.currentPage >= pagination.numPages}
                    onClick={() => handlePageChange(pagination.currentPage + 1)}
                  >
                    Next
                    <ChevronRightIcon />
                  </Button>
                </Flex>
              </Flex>
            )}
          </>
        )}
      </Flex>

      {/* Add contacts dialog */}
      <Dialog.Root
        open={isAddContactsOpen}
        onOpenChange={(open) => {
          setIsAddContactsOpen(open);
          if (!open) resetAddContactsFlow();
        }}
      >
        <Dialog.Content maxWidth={addContactsMode === "choice" ? "560px" : "720px"}>
          <Dialog.Title>Add contacts</Dialog.Title>
          <Dialog.Description size="2" color="gray">
            {addContactsMode === "choice" && "Choose how you want to add contacts to your audience."}
            {addContactsMode === "import" && "Upload a CSV and tell us which system exported it."}
            {addContactsMode === "manual" && "Enter the contact details manually."}
          </Dialog.Description>

          {addContactsMode === "choice" && (
            <Flex gap="3" mt="4" wrap="wrap">
              <Card
                role="button"
                tabIndex={0}
                onClick={() => setAddContactsMode("import")}
                onKeyDown={(event) => handleKeyboardActivation(event, () => setAddContactsMode("import"))}
                style={{ flex: "1 1 220px", cursor: "pointer", padding: "var(--space-4)" }}
              >
                <Flex direction="column" gap="3" align="start">
                  <Flex align="center" gap="2">
                    <PlusIcon />
                    <Text size="3" weight="medium">Import contacts</Text>
                  </Flex>
                  <Text size="2" color="gray">CSV upload</Text>
                </Flex>
              </Card>
              <Card
                role="button"
                tabIndex={0}
                onClick={() => setAddContactsMode("manual")}
                onKeyDown={(event) => handleKeyboardActivation(event, () => setAddContactsMode("manual"))}
                style={{ flex: "1 1 220px", cursor: "pointer", padding: "var(--space-4)" }}
              >
                <Flex direction="column" gap="3" align="start">
                  <Flex align="center" gap="2">
                    <PersonIcon />
                    <Text size="3" weight="medium">Add a single contact</Text>
                  </Flex>
                  <Text size="2" color="gray">Manual entry</Text>
                </Flex>
              </Card>
              <Flex mt="4" justify="end" style={{ width: "100%" }}>
                <Dialog.Close>
                  <Button variant="soft" color="gray">
                    Cancel
                  </Button>
                </Dialog.Close>
              </Flex>
            </Flex>
          )}

          {addContactsMode === "import" && (
            <Flex direction="column" gap="4" mt="4">
              <Callout.Root color="amber">
                <Callout.Icon>
                  <InfoCircledIcon />
                </Callout.Icon>
                <Callout.Text>
                  Upload comma-separated CSV files only. Semicolon- or tab-separated exports cannot be processed.
                </Callout.Text>
              </Callout.Root>

              <Flex direction="column" gap="2">
                <Text size="2" weight="medium">CSV source</Text>
                <Select.Root value={csvSource} onValueChange={setCsvSource}>
                  <Select.Trigger placeholder="CSV source" />
                  <Select.Content>
                    {CONTACT_IMPORT_SOURCE_OPTIONS.map((source) => (
                      <Select.Item key={source.value} value={source.value}>{source.label}</Select.Item>
                    ))}
                  </Select.Content>
                </Select.Root>
                {csvSource === "manual" && (
                  <Flex gap="4" align="center">
                    <Button asChild variant="soft" size="1" color="gray" style={{ alignSelf: "flex-start" }}>
                      <a href={MANUAL_CSV_SAMPLE_HREF} download="manual-contacts-sample.csv">
                        <DownloadIcon />
                        Download sample CSV
                      </a>
                    </Button>
                    <Tooltip
                      content={(
                        <Flex direction="column" gap="1" style={{ maxWidth: 320 }}>
                          <Text size="1">Use the sample headers for manual CSV uploads.</Text>
                          <Text size="1">Email, SMS, and WhatsApp opt-in accept true, false, yes, no, 1, 0, subscribed, or unsubscribed.</Text>
                          <Text size="1">Visits and visit dates update the selected default location for matching email contacts.</Text>
                          <Text size="1">Tags accepts comma or semicolon separated names (e.g. "VIP; Regulars"); missing tags are created automatically.</Text>
                        </Flex>
                      )}
                    >
                      <Button type="button" variant="ghost" color="gray" size="1" aria-label="Manual CSV upload rules">
                        <InfoCircledIcon />
                      </Button>
                    </Tooltip>
                  </Flex>
                )}
              </Flex>

              <Flex direction="column" gap="2">
                <Text size="2" weight="medium">Default location</Text>
                <Select.Root value={csvLocationId} onValueChange={setCsvLocationId}>
                  <Select.Trigger placeholder="Default location" />
                  <Select.Content>
                    <Select.Item value="none">No default location</Select.Item>
                    {locationOptions.map((loc) => (
                      <Select.Item key={loc.id} value={String(loc.id)}>{loc.name}</Select.Item>
                    ))}
                  </Select.Content>
                </Select.Root>
              </Flex>

              <Flex direction="column" gap="2">
                <Text size="2" weight="medium">CSV file</Text>
                <input
                  type="file"
                  accept=".csv,text/csv"
                  onChange={(event) => setCsvFile(event.target.files?.[0] || null)}
                  style={{
                    width: "100%",
                    padding: "var(--space-2)",
                    border: "1px solid var(--gray-a6)",
                    borderRadius: "var(--radius-2)",
                  }}
                />
                {csvFile && <Text size="1" color="gray">{csvFile.name}</Text>}
              </Flex>

              <Flex gap="3" justify="between" align="center" mt="2">
                <Button variant="soft" color="gray" onClick={() => setAddContactsMode("choice")}>Back</Button>
                <Flex gap="3">
                  <Dialog.Close>
                    <Button variant="soft" color="gray">Cancel</Button>
                  </Dialog.Close>
                  <Button disabled={importContactsMutation.isPending} onClick={handleImportContacts}>
                    {importContactsMutation.isPending ? "Starting..." : "Import contacts"}
                  </Button>
                </Flex>
              </Flex>
            </Flex>
          )}

          {addContactsMode === "manual" && (
            <Flex direction="column" gap="4" mt="4">
              <Flex gap="3" wrap="wrap">
                <Flex direction="column" gap="2" style={{ flex: "1 1 220px" }}>
                  <Text size="2" weight="medium">First name</Text>
                  <TextField.Root value={manualContact.first_name} onChange={(e) => updateManualContact("first_name", e.target.value)} />
                </Flex>
                <Flex direction="column" gap="2" style={{ flex: "1 1 220px" }}>
                  <Text size="2" weight="medium">Last name</Text>
                  <TextField.Root value={manualContact.last_name} onChange={(e) => updateManualContact("last_name", e.target.value)} />
                </Flex>
              </Flex>

              <Flex gap="3" wrap="wrap">
                <Flex direction="column" gap="2" style={{ flex: "1 1 220px" }}>
                  <Text size="2" weight="medium">Email address</Text>
                  <TextField.Root type="email" value={manualContact.email_address} onChange={(e) => updateManualContact("email_address", e.target.value)} />
                </Flex>
                <Flex direction="column" gap="2" style={{ flex: "1 1 220px" }}>
                  <Text size="2" weight="medium">Phone number</Text>
                  <TextField.Root value={manualContact.phone_number} onChange={(e) => updateManualContact("phone_number", e.target.value)} />
                </Flex>
              </Flex>

              <Flex gap="3" wrap="wrap">
                <Flex direction="column" gap="2" style={{ flex: "1 1 180px" }}>
                  <Text size="2" weight="medium">Gender</Text>
                  <Select.Root value={manualContact.gender} onValueChange={(value) => updateManualContact("gender", value)}>
                    <Select.Trigger />
                    <Select.Content>
                      <Select.Item value="male">Male</Select.Item>
                      <Select.Item value="female">Female</Select.Item>
                      <Select.Item value="other">Other</Select.Item>
                      <Select.Item value="prefer_not_to_respond">Prefer not to respond</Select.Item>
                      <Select.Item value="no_response">No response</Select.Item>
                    </Select.Content>
                  </Select.Root>
                </Flex>
                <Flex direction="column" gap="2" style={{ flex: "1 1 180px" }}>
                  <Text size="2" weight="medium">Age group</Text>
                  <Select.Root value={manualContact.age_group} onValueChange={(value) => updateManualContact("age_group", value)}>
                    <Select.Trigger />
                    <Select.Content>
                      {Object.entries(AGE_GROUP_LABELS).map(([value, label]) => (
                        <Select.Item key={value} value={value}>{label}</Select.Item>
                      ))}
                    </Select.Content>
                  </Select.Root>
                </Flex>
                <Flex direction="column" gap="2" style={{ flex: "1 1 180px" }}>
                  <Text size="2" weight="medium">Date of birth</Text>
                  <TextField.Root type="date" value={manualContact.dob} onChange={(e) => updateManualContact("dob", e.target.value)} />
                </Flex>
              </Flex>

              <Flex gap="3" wrap="wrap">
                <Flex direction="column" gap="2" style={{ flex: "1 1 220px" }}>
                  <Text size="2" weight="medium">Source</Text>
                  <Select.Root value={manualContact.source} onValueChange={(value) => updateManualContact("source", value)}>
                    <Select.Trigger />
                    <Select.Content>
                      {CONTACT_SOURCE_OPTIONS.map((source) => (
                        <Select.Item key={source.value} value={source.value}>{source.label}</Select.Item>
                      ))}
                    </Select.Content>
                  </Select.Root>
                </Flex>
                <Flex direction="column" gap="2" style={{ flex: "1 1 220px" }}>
                  <Text size="2" weight="medium">Marketing consent</Text>
                  <Flex gap="4" align="center" style={{ minHeight: 32 }}>
                    <Text as="label" size="2">
                      <Flex gap="2" align="center">
                        <Checkbox checked={manualContact.email_marketing} onCheckedChange={(checked) => updateManualContact("email_marketing", Boolean(checked))} />
                        Email
                      </Flex>
                    </Text>
                    <Text as="label" size="2">
                      <Flex gap="2" align="center">
                        <Checkbox checked={manualContact.sms_marketing} onCheckedChange={(checked) => updateManualContact("sms_marketing", Boolean(checked))} />
                        SMS
                      </Flex>
                    </Text>
                    <Text as="label" size="2">
                      <Flex gap="2" align="center">
                        <Checkbox checked={manualContact.whatsapp_marketing} onCheckedChange={(checked) => updateManualContact("whatsapp_marketing", Boolean(checked))} />
                        WhatsApp
                      </Flex>
                    </Text>
                  </Flex>
                </Flex>
              </Flex>

              <Flex direction="column" gap="2">
                <Text size="2" weight="medium">Tags</Text>
                {contactTags.length > 0 ? (
                  <Flex gap="1" wrap="wrap">
                    {contactTags.map((tag) => {
                      const active = manualContact.tag_ids.includes(tag.tag_id);
                      return (
                        <Badge
                          key={tag.tag_id}
                          variant={active ? "solid" : "outline"}
                          color={active ? "blue" : "gray"}
                          style={{ cursor: "pointer", userSelect: "none" }}
                          onClick={() => {
                            const next = active
                              ? manualContact.tag_ids.filter((id) => id !== tag.tag_id)
                              : [...manualContact.tag_ids, tag.tag_id];
                            updateManualContact("tag_ids", next);
                          }}
                        >
                          {tag.name}
                        </Badge>
                      );
                    })}
                  </Flex>
                ) : (
                  <Text size="2" color="gray">No tags yet — create them in the Tags tab</Text>
                )}
              </Flex>

              <Flex direction="column" gap="2">
                <Text size="2" weight="medium">Locations</Text>
                <Box style={{ border: "1px solid var(--gray-a6)", borderRadius: "var(--radius-2)", padding: "var(--space-2)", maxHeight: 144, overflow: "auto" }}>
                  {locationOptions.length > 0 ? (
                    <CheckboxGroup.Root
                      value={manualContact.location_ids}
                      onValueChange={(value) => updateManualContact("location_ids", value)}
                      style={{ display: "flex", flexDirection: "column", gap: "var(--space-1)" }}
                    >
                      {locationOptions.map((loc) => (
                        <Flex key={loc.id} p="2">
                          <CheckboxGroup.Item value={String(loc.id)}>{loc.name}</CheckboxGroup.Item>
                        </Flex>
                      ))}
                    </CheckboxGroup.Root>
                  ) : (
                    <Text size="2" color="gray">No locations available</Text>
                  )}
                </Box>
              </Flex>

              <Flex gap="3" justify="between" align="center" mt="2">
                <Button variant="soft" color="gray" onClick={() => setAddContactsMode("choice")}>Back</Button>
                <Flex gap="3">
                  <Dialog.Close>
                    <Button variant="soft" color="gray">Cancel</Button>
                  </Dialog.Close>
                  <Button disabled={createContactMutation.isPending} onClick={handleManualContactSubmit}>
                    {createContactMutation.isPending ? "Saving..." : "Create contact"}
                  </Button>
                </Flex>
              </Flex>
            </Flex>
          )}
        </Dialog.Content>
      </Dialog.Root>

      {/* Email suppression management */}
      <Dialog.Root
        open={isSuppressionsOpen}
        onOpenChange={(open) => {
          setIsSuppressionsOpen(open);
          if (!open) resetSuppressionClear();
        }}
      >
        <Dialog.Content maxWidth="1050px"
          style={{ height: "min(760px, calc(100vh - 96px))", display: "flex", flexDirection: "column", 
          overflow: "hidden" }}>
          <Flex style={{ alignItems: "center", justifyContent: "space-between" }}>
            <Dialog.Title style={{ display: "flex", alignItems: "center" }}>Email Suppression Management</Dialog.Title>
            <Dialog.Close>
              <Button size="2" variant="soft" color="gray">
                Close
              </Button>
            </Dialog.Close>
          </Flex>
          <Dialog.Description size="2" color="gray">
            Review addresses blocked from email delivery. Clearing a suppression is owner-only and permanently audited.
          </Dialog.Description>

          <Flex direction="column" gap="3" mt="4" style={{ flex: "1 1 auto", minHeight: 0 }}>
            {/* Filters */}
            <Flex gap="3" align="center" wrap="wrap">
              <TextField.Root
                placeholder="Search email or contact..."
                value={suppressionFilters.search}
                onChange={(event) => setSuppressionFilters((prev) => ({ ...prev, search: event.target.value, page: 1 }))}
                style={{ width: 260 }}
>
                <TextField.Slot><MagnifyingGlassIcon /></TextField.Slot>
              </TextField.Root>
              <Select.Root
                value={suppressionFilters.active}
                onValueChange={(value) => setSuppressionFilters((prev) => ({ ...prev, active: value, page: 1 }))}
              >
                <Select.Trigger style={{ width: 150 }} />
                <Select.Content>
                  <Select.Item value="true">Active</Select.Item>
                  <Select.Item value="false">Cleared</Select.Item>
                  <Select.Item value="all">All records</Select.Item>
                </Select.Content>
              </Select.Root>
              <Select.Root
                value={suppressionFilters.reason || "all"}
                onValueChange={(value) => setSuppressionFilters((prev) => ({ ...prev, reason: value === "all" ? "" : value, page: 1 }))}
              >
                <Select.Trigger style={{ width: 190 }} />
                <Select.Content>
                  <Select.Item value="all">All reasons</Select.Item>
                  <Select.Item value="hard_bounce">Hard bounce</Select.Item>
                  <Select.Item value="dropped">Permanent drop</Select.Item>
                  <Select.Item value="spam_report">Spam report</Select.Item>
                </Select.Content>
              </Select.Root>
              <Badge color={canManageSuppressions ? "green" : "gray"} variant="soft">
                {canManageSuppressions ? "Owner controls enabled" : "Read only"}
              </Badge>
            </Flex>

            {/*  */}
            <Box style={{ flex: "1 1 auto", minHeight: 0, overflow: "auto" }}>
              {suppressionsLoading ? (
                <Flex align="center" justify="center" style={{ minHeight: 180 }}>
                  <Text size="2" color="gray">Loading suppressions...</Text>
                </Flex>
              ) : suppressions.length > 0 ? (
                <Table.Root variant="surface" layout="fixed">
                  <Table.Header>
                    <Table.Row>
                      <Table.ColumnHeaderCell style={{ width: 220 }}>Email</Table.ColumnHeaderCell>
                      <Table.ColumnHeaderCell style={{ width: 145 }}>Reason</Table.ColumnHeaderCell>
                      <Table.ColumnHeaderCell style={{ width: 145 }}>Contact</Table.ColumnHeaderCell>
                      <Table.ColumnHeaderCell style={{ width: 165 }}>Recorded</Table.ColumnHeaderCell>
                      <Table.ColumnHeaderCell>Recent audit</Table.ColumnHeaderCell>
                      <Table.ColumnHeaderCell style={{ width: 95 }} />
                    </Table.Row>
                  </Table.Header>
                  <Table.Body>
                    {suppressions.map((suppression) => {
                      const latestAudit = suppression.audit_events?.[0];
                      return (
                        <Table.Row key={suppression.id}>
                          <Table.Cell>
                            <Flex direction="column" gap="1" style={{ minWidth: 0 }}>
                              <TruncatedText size="2" weight="medium">{suppression.email_address}</TruncatedText>
                              <Badge color={suppression.active ? "red" : "gray"} variant="soft" size="1" style={{ width: "fit-content" }}>
                                {suppression.active ? "Active" : "Cleared"}
                              </Badge>
                            </Flex>
                          </Table.Cell>
                          <Table.Cell>
                            <Badge color={suppression.reason === "spam_report" ? "orange" : "red"} variant="soft">
                              {suppression.reason_label}
                            </Badge>
                          </Table.Cell>
                          <Table.Cell>
                            <Flex direction="column" gap="1">
                              <TruncatedText size="2">{suppression.contact?.full_name || "—"}</TruncatedText>
                            </Flex>
                          </Table.Cell>
                          <Table.Cell><Text size="1" color="gray">{formatFullDateTime(suppression.occurred_at)}</Text></Table.Cell>
                          <Table.Cell>
                            {latestAudit ? (
                              <Flex direction="column" gap="1">
                                <Text size="1" weight="medium">
                                  {latestAudit.action === "cleared" ? "Cleared" : "Applied"}
                                  {latestAudit.actor ? ` by ${latestAudit.actor}` : " by provider"}
                                </Text>
                                <TruncatedText size="1" color="gray">{latestAudit.note || latestAudit.source}</TruncatedText>
                              </Flex>
                            ) : <Text size="1" color="gray">—</Text>}
                          </Table.Cell>
                          <Table.Cell>
                            {suppression.active && canManageSuppressions && (
                              <Button size="1" color="red" variant="soft" onClick={() => {
                                resetSuppressionClear();
                                setSuppressionToClear(suppression);
                              }}>
                                Clear
                              </Button>
                            )}
                          </Table.Cell>
                        </Table.Row>
                      );
                    })}
                  </Table.Body>
                </Table.Root>
              ) : (
                <Flex align="center" justify="center" style={{ height: '100%', border: "1px dashed var(--gray-a6)", 
                  borderRadius: "var(--radius-3)" }}>
                  <Text size="2" color="gray">No suppression records match these filters</Text>
                </Flex>
              )}
            </Box>

            <Flex justify="between" align="center" pt="3">
              <Text size="1" color="gray">
                {suppressionPagination.count.toLocaleString()} record{suppressionPagination.count === 1 ? "" : "s"}
              </Text>
              <Flex gap="2" align="center">
                <Button
                  size="1"
                  variant="soft"
                  color="gray"
                  disabled={suppressionPagination.currentPage <= 1}
                  onClick={() => setSuppressionFilters((prev) => ({ ...prev, page: prev.page - 1 }))}
                >
                  Previous
                </Button>
                <Text size="1" color="gray">Page {suppressionPagination.currentPage} of {suppressionPagination.numPages}</Text>
                <Button
                  size="1"
                  variant="soft"
                  color="gray"
                  disabled={suppressionPagination.currentPage >= suppressionPagination.numPages}
                  onClick={() => setSuppressionFilters((prev) => ({ ...prev, page: prev.page + 1 }))}
                >
                  Next
                </Button>
              </Flex>
            </Flex>
          </Flex>
        </Dialog.Content>
      </Dialog.Root>

      <AlertDialog.Root open={!!suppressionToClear} onOpenChange={(open) => !open && resetSuppressionClear()}>
        <AlertDialog.Content maxWidth="540px">
          <AlertDialog.Title>Clear email suppression?</AlertDialog.Title>
          <AlertDialog.Description size="2">
            This allows <Text weight="bold">{suppressionToClear?.email_address}</Text> to become eligible again if consent and email validity also permit it. Provider suppression lists may still reject delivery.
          </AlertDialog.Description>
          <Flex direction="column" gap="3" mt="4">
            <Flex direction="column" gap="1">
              <Text size="2" weight="medium">Reason for clearing</Text>
              <TextArea
                placeholder="For example: address corrected and independently verified"
                value={suppressionClearNote}
                onChange={(event) => setSuppressionClearNote(event.target.value)}
                resize="vertical"
              />
            </Flex>
            <Text as="label" size="2">
              <Flex gap="2" align="start">
                <Checkbox checked={suppressionRiskAcknowledged} onCheckedChange={(value) => setSuppressionRiskAcknowledged(Boolean(value))} />
                I understand that clearing this protection can cause another rejection or a compliance complaint.
              </Flex>
            </Text>
          </Flex>
          <Flex gap="3" mt="4" justify="end">
            <AlertDialog.Cancel><Button variant="soft" color="gray">Cancel</Button></AlertDialog.Cancel>
            <Button
              color="red"
              disabled={suppressionClearNote.trim().length < 5 || !suppressionRiskAcknowledged || clearSuppressionMutation.isPending}
              onClick={handleClearSuppression}
            >
              {clearSuppressionMutation.isPending ? "Clearing..." : "Clear suppression"}
            </Button>
          </Flex>
        </AlertDialog.Content>
      </AlertDialog.Root>

      {/* Contact details dialog */}
      <Dialog.Root
        open={isContactDetailsOpen}
        onOpenChange={(open) => {
          setIsContactDetailsOpen(open);
          if (!open) {
            setSelectedContact(null);
            setContactDetails(null);
            setContactEmailActivity([]);
            setContactEmailActivityCount(0);
          }
        }}
      >
        <Dialog.Content
          maxWidth="920px"
          style={{
            height: "min(760px, calc(100vh - 96px))",
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
          }}
        >
          <Dialog.Title>{contactDetails?.full_name || selectedContact?.full_name || "Contact details"}</Dialog.Title>
          <Dialog.Description size="2" color="gray">
            Full audience profile, marketing consent, location visits, and email activity journey.
          </Dialog.Description>

          {isContactDetailsLoading ? (
            <Flex align="center" justify="center" style={{ flex: "1 1 auto", minHeight: 0 }}>
              <Text size="2" color="gray">Loading contact details...</Text>
            </Flex>
          ) : (
            (() => {
              const contact = contactDetails || selectedContact || {};
              const locationVisits = contact.location_visits || [];
              const totalVisits = locationVisits.reduce((total, visit) => total + (visit.visit_count || 0), 0);

              return (
                <Flex direction="column" gap="4" mt="4" style={{ flex: "1 1 auto", minHeight: 0 }}>
                  <Box style={{ flex: "1 1 auto", minHeight: 0, overflow: "auto", paddingRight: "var(--space-2)" }}>
                    <Flex direction="column" gap="4">
                      <Card>
                        <Flex direction="column" gap="3">
                          <Box style={DETAIL_GRID_STYLE}>
                            <DetailField label="First Name">
                              <TruncatedText size="2" color="gray">{contact.first_name || "—"}</TruncatedText>
                            </DetailField>
                            <DetailField label="Last Name">
                              <TruncatedText size="2" color="gray">{contact.last_name || "—"}</TruncatedText>
                            </DetailField>
                            <DetailField label="Email">
                              <TruncatedText size="2" color="gray">
                                {contact.email_is_placeholder ? "No email" : contact.email_address || "—"}
                              </TruncatedText>
                            </DetailField>
                            <DetailField label="Email validation">
                              <EmailValidationBadge contact={contact} showDetails />
                            </DetailField>
                            <DetailField label="Age Group">
                              <Text size="2" color="gray">{AGE_GROUP_LABELS[contact.age_group] || contact.age_group || "—"}</Text>
                            </DetailField>
                            <DetailField label="Gender">
                              <Text size="2" color="gray">{GENDER_LABELS[contact.gender] || contact.gender || "—"}</Text>
                            </DetailField>
                            <DetailField label="Phone">
                              <TruncatedText size="2" color="gray">{formatInternationalPhoneNumber(contact.phone_number) || "—"}</TruncatedText>
                            </DetailField>
                            <DetailField label="Date of Birth">
                              <Text size="2" color="gray">{formatDateTime(contact.dob)}</Text>
                            </DetailField>
                            <DetailField label="Source">
                              <Badge color={SOURCE_COLORS[contact.source] || "gray"} variant="soft" size="1">
                                {SOURCE_LABELS[contact.source] || contact.source || "—"}
                              </Badge>
                            </DetailField>
                            <DetailField label="Email Consent">
                              <ConsentBadge value={contact.email_marketing} />
                            </DetailField>
                            <DetailField label="Delivery state">
                              {contact.email_suppression?.active ? (
                                <Flex direction="column" gap="1" align="start">
                                  <Badge color="red" variant="soft">Suppressed — {contact.email_suppression.reason_label}</Badge>
                                  <Text size="1" color="gray">Since {formatFullDateTime(contact.email_suppression.occurred_at)}</Text>
                                </Flex>
                              ) : (
                                <Badge color="green" variant="soft">No suppression</Badge>
                              )}
                            </DetailField>
                            <DetailField label="SMS Consent">
                              <ConsentBadge value={contact.sms_marketing} />
                            </DetailField>
                            <DetailField label="WhatsApp Consent">
                              <ConsentBadge value={contact.whatsapp_marketing} />
                            </DetailField>
                            <DetailField label="Total Visits">
                              <Text size="2" color="gray">{totalVisits}</Text>
                            </DetailField>
                            {contact.source === "sevenrooms" && (
                              <>
                                <DetailField label="SevenRooms visits">
                                  <Text size="2" color="gray">
                                    {contact.sevenrooms_visits == null ? "—" : contact.sevenrooms_visits}
                                  </Text>
                                </DetailField>
                                <DetailField label="SevenRooms spend">
                                  <Text size="2" weight="medium">
                                    {formatSevenRoomsSpend(contact.sevenrooms_spend, contact.sevenrooms_spend_currency)}
                                  </Text>
                                </DetailField>
                              </>
                            )}
                            {contact.source === "eat_app" && (
                              <>
                                <DetailField label="Eat App visits">
                                  <Text size="2" color="gray">
                                    {contact.eat_app_visits == null ? "—" : contact.eat_app_visits}
                                  </Text>
                                </DetailField>
                                <DetailField label="Eat App spend">
                                  <Text size="2" color="gray">
                                    {formatSevenRoomsSpend(contact.eat_app_spend)}
                                  </Text>
                                </DetailField>
                              </>
                            )}
                            <DetailField label="Tags" style={{ gridColumn: "span 6" }}>
                              <Flex gap="2" align="center" wrap="wrap" justify="end">
                                {(contact.tags || []).map((tag) => (
                                  <Badge key={tag.tag_id} variant="soft" size="1">{tag.name}</Badge>
                                ))}
                                {!(contact.tags || []).length && <Text size="2" color="gray">—</Text>}
                                <Popover.Root>
                                  <Popover.Trigger>
                                    <Button variant="soft" color="gray" size="1">
                                      <Pencil1Icon width="12" height="12" />
                                      Edit
                                    </Button>
                                  </Popover.Trigger>
                                  <Popover.Content maxHeight="280px" style={{ width: "220px" }}>
                                    {contactTags.length > 0 ? (
                                      <Flex direction="column" gap="1">
                                        {contactTags.map((tag) => {
                                          const checked = (contact.tags || []).some((t) => t.tag_id === tag.tag_id);
                                          return (
                                            <Text as="label" size="2" key={tag.tag_id}>
                                              <Flex gap="2" align="center" p="1">
                                                <Checkbox
                                                  checked={checked}
                                                  disabled={updateContactMutation.isPending}
                                                  onCheckedChange={(value) => handleContactTagsChange(tag.tag_id, Boolean(value))}
                                                />
                                                {tag.name}
                                              </Flex>
                                            </Text>
                                          );
                                        })}
                                      </Flex>
                                    ) : (
                                      <Text size="2" color="gray">No tags yet — create them in the Tags tab</Text>
                                    )}
                                  </Popover.Content>
                                </Popover.Root>
                              </Flex>
                            </DetailField>
                            <DetailField label="Created On" style={{ gridColumn: "span 3" }}>
                              <Text size="2" color="gray">{formatFullDateTime(contact.created_at)}</Text>
                            </DetailField>
                            <DetailField label="Last Updated" style={{ gridColumn: "span 3" }}>
                              <Text size="2" color="gray">{formatFullDateTime(contact.updated_at)}</Text>
                            </DetailField>
                          </Box>
                        </Flex>
                      </Card>

                      <Box>
                        <Text weight="bold">Location visits</Text>
                        <Box mt="2">
                          {locationVisits.length > 0 ? (
                            <Table.Root variant="surface">
                              <Table.Header>
                                <Table.Row>
                                  <Table.ColumnHeaderCell>Location</Table.ColumnHeaderCell>
                                  <Table.ColumnHeaderCell>Visits</Table.ColumnHeaderCell>
                                  <Table.ColumnHeaderCell>First Visit</Table.ColumnHeaderCell>
                                  <Table.ColumnHeaderCell>Last Visit</Table.ColumnHeaderCell>
                                </Table.Row>
                              </Table.Header>
                              <Table.Body>
                                {locationVisits.map((visit) => (
                                  <Table.Row key={visit.id}>
                                    <Table.Cell>{visit.location_name || "—"}</Table.Cell>
                                    <Table.Cell>{visit.visit_count || 0}</Table.Cell>
                                    <Table.Cell>{formatFullDateTime(visit.first_seen)}</Table.Cell>
                                    <Table.Cell>{formatFullDateTime(visit.last_seen)}</Table.Cell>
                                  </Table.Row>
                                ))}
                              </Table.Body>
                            </Table.Root>
                          ) : (
                            <Flex align="center" justify="center" style={{ border: "1px dashed var(--gray-a6)", borderRadius: "var(--radius-2)", minHeight: 84 }}>
                              <Text size="2" color="gray">No location visits recorded</Text>
                            </Flex>
                          )}
                        </Box>
                      </Box>

                      <Box>
                        <Flex justify="between" align="end" gap="3" wrap="wrap">
                          <Flex direction="column" gap="1">
                            <Flex align="center" gap="2">
                              <EnvelopeClosedIcon />
                              <Text weight="bold">Email activity journey</Text>
                            </Flex>
                            <Text size="1" color="gray">
                              Latest {Math.min(contactEmailActivityCount, 100)} of {contactEmailActivityCount} events, newest first. Repeated opens and clicks remain visible.
                            </Text>
                          </Flex>
                          {contactEmailActivity.some((activity) => activity.is_test) && (
                            <Badge color="amber" variant="soft">Includes test activity</Badge>
                          )}
                        </Flex>
                        <Box mt="2">
                          {contactEmailActivity.length > 0 ? (
                            <Table.Root variant="surface" layout="fixed">
                              <Table.Header>
                                <Table.Row>
                                  <Table.ColumnHeaderCell style={{ width: 135 }}>Event</Table.ColumnHeaderCell>
                                  <Table.ColumnHeaderCell>Email</Table.ColumnHeaderCell>
                                  <Table.ColumnHeaderCell style={{ width: 140 }}>Location</Table.ColumnHeaderCell>
                                  <Table.ColumnHeaderCell style={{ width: 180 }}>Time</Table.ColumnHeaderCell>
                                </Table.Row>
                              </Table.Header>
                              <Table.Body>
                                {contactEmailActivity.map((activity) => (
                                  <Table.Row key={activity.id}>
                                    <Table.Cell>
                                      <Flex gap="1" align="center" wrap="wrap">
                                        <Badge color={EMAIL_ACTIVITY_COLORS[activity.event] || "gray"} variant="soft">
                                          {EMAIL_ACTIVITY_LABELS[activity.event] || activity.event}
                                        </Badge>
                                        {activity.is_test && <Badge color="amber" variant="solid">Test</Badge>}
                                      </Flex>
                                    </Table.Cell>
                                    <Table.Cell>
                                      <Flex direction="column" gap="1" style={{ minWidth: 0 }}>
                                        <TruncatedText size="2" weight="medium">
                                          {activity.campaign?.name || activity.template?.name || activity.subject || "Email activity"}
                                        </TruncatedText>
                                        {(activity.campaign?.name || activity.template?.name) && activity.subject && (
                                          <TruncatedText size="1" color="gray">{activity.subject}</TruncatedText>
                                        )}
                                      </Flex>
                                    </Table.Cell>
                                    <Table.Cell>
                                      <TruncatedText size="2" color="gray">{activity.location?.name || "—"}</TruncatedText>
                                    </Table.Cell>
                                    <Table.Cell>
                                      <Text size="1" color="gray">{formatFullDateTime(activity.event_timestamp)}</Text>
                                    </Table.Cell>
                                  </Table.Row>
                                ))}
                              </Table.Body>
                            </Table.Root>
                          ) : (
                            <Flex align="center" justify="center" style={{ border: "1px dashed var(--gray-a6)", borderRadius: "var(--radius-2)", minHeight: 84 }}>
                              <Text size="2" color="gray">No email activity recorded for this contact</Text>
                            </Flex>
                          )}
                        </Box>
                      </Box>
                    </Flex>
                  </Box>

                  <Flex gap="3" justify="between" align="center" pt="3" style={{ borderTop: "1px solid var(--gray-a5)", flex: "0 0 auto" }}>
                    <Button
                      variant="soft"
                      color="red"
                      onClick={() => setContactToDelete(contact)}
                    >
                      <TrashIcon />
                      Delete contact
                    </Button>
                    <Dialog.Close>
                      <Button variant="soft" color="gray">Close</Button>
                    </Dialog.Close>
                  </Flex>
                </Flex>
              );
            })()
          )}
        </Dialog.Content>
      </Dialog.Root>

      {/* Delete confirmation dialog */}
      <AlertDialog.Root open={!!contactToDelete} onOpenChange={(open) => !open && setContactToDelete(null)}>
        <AlertDialog.Content maxWidth="420px">
          <AlertDialog.Title>Delete Contact</AlertDialog.Title>
          <AlertDialog.Description size="2">
            Are you sure you want to delete{" "}
            <Text weight="bold">{contactToDelete?.full_name || contactToDelete?.email_address}</Text>?
            This action cannot be undone.
          </AlertDialog.Description>
          <Flex gap="3" mt="4" justify="end">
            <AlertDialog.Cancel>
              <Button variant="soft" color="gray">Cancel</Button>
            </AlertDialog.Cancel>
            <AlertDialog.Action>
              <Button variant="solid" color="red" onClick={handleDelete}>Delete</Button>
            </AlertDialog.Action>
          </Flex>
        </AlertDialog.Content>
      </AlertDialog.Root>

      <ToastAlert />
    </>
  );
}
